import type { LucideIcon, LucideProps } from 'lucide-react';
import { ICON_MAP, type IconName } from './icon-map';
import { cn } from './cn';

export type { IconName } from './icon-map';

interface Props extends Omit<LucideProps, 'name'> {
  name: IconName;
}

/**
 * Löst einen Icon-Namen über die statische Map auf (Tree-Shaking, vgl. icon-map.ts).
 * Unbekannte Namen (z. B. aus API-Daten) fallen auf HelpCircle zurück —
 * im Dev-Modus mit Warnung, in Produktion still und ohne Crash.
 */
function resolveIcon(name: IconName): LucideIcon {
  const found = (ICON_MAP as Record<string, LucideIcon | undefined>)[name];
  if (found) return found;
  if (import.meta.env.DEV) {
    console.warn(
      `[Icon] Unbekanntes Icon "${String(name)}" — Fallback "HelpCircle". Bitte in lib/icon-map.ts ergänzen.`,
    );
  }
  return ICON_MAP.HelpCircle;
}

/**
 * Plan §4.4 — global Icon-Wrapper.
 * Defaults: size 18, strokeWidth 1.5, color currentColor (via text-ink-600).
 * NIE direkt aus lucide-react importieren — immer dieser Wrapper.
 */
export function Icon({ name, size = 18, strokeWidth = 1.5, className, ...rest }: Props) {
  const Cmp = resolveIcon(name);
  return (
    <Cmp
      size={size}
      strokeWidth={strokeWidth}
      className={cn('shrink-0', className)}
      aria-hidden="true"
      {...rest}
    />
  );
}
